import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { Form, FormProps, Button, Alert, Checkbox } from 'antd';
import api from 'base/utils/api';
import { useStore } from 'base/store';
import { UserStateType } from 'base/store/userStore';
import { useRouter } from 'next/router';
import FormEmail from '@base/snippets/FormEmail';
import FormNewPassword from '@base/snippets/FormNewPassword';
import FormRegisterAgree from '@base/snippets/FormRegisterAgree';
import { RouteEnum } from 'pro/config/routers';
import { getTrackerClientIds, trackRegister } from 'base/utils/tracker';
import { Trans, useTrans } from 'base/utils/i18n';

type RegisterFormProps = FormProps & {
    showLabel?: boolean;
    extendValues?: Record<string, number | string | boolean | undefined>;
    onLoginSuccess?: () => void;
};

const RegisterForm: React.FC<RegisterFormProps> = props => {
    const {
        showLabel = true,
        extendValues,
        onLoginSuccess,
        ...formProps
    } = props;
    const { userStore, miniCartStore } = useStore();
    const router = useRouter();
    const [form] = Form.useForm();
    const { t } = useTrans();

    const [errorMsg, setErrorMsg] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);

    const onFinish = async (values: any) => {
        setErrorMsg('');
        setLoading(true);
        try {
            const trackerClientIds = await getTrackerClientIds();
            await api.post('/web/user/register', {
                data: {
                    ...values,
                    ...extendValues,
                    ...trackerClientIds
                }
            }).then(async (res: Partial<UserStateType>) => {
                trackRegister();
                userStore.login(res);
                await userStore.fetchUserInfo();

                if (onLoginSuccess) {
                    onLoginSuccess?.()
                } else {
                    miniCartStore.reload();
                    router.push(router.query.redirect ? decodeURIComponent(router.query.redirect as string) : RouteEnum.SHOP);
                }
            })

        } catch (e) {
            setErrorMsg(e.message);
        }
        setLoading(false);
    };

    useEffect(() => {
        if (extendValues?.shopifyEmail) {
            form.setFieldsValue({ email: extendValues.shopifyEmail });
        }
    }, []);

    return (
        <>
            <Alert
                message={errorMsg}
                type="error"
                className={cn({ 'hide': !errorMsg }, 'mb-2')}/>

            <Form
                {...formProps}
                name="register"
                layout="vertical"
                onFinish={onFinish}
                autoComplete="off"
                size="large"
                requiredMark={false}
                form={form}
            >
                <FormEmail showLabel={showLabel}/>
                <FormNewPassword showLabel={showLabel}/>

                <FormRegisterAgree/>

                <Form.Item noStyle>
                    <Button
                        type="primary"
                        htmlType="submit"
                        block
                        loading={loading}
                        className="mt-2 mb-4"
                    >
                        {t('web_register_btn_register')}
                    </Button>
                </Form.Item>
            </Form>
        </>
    );
};

export default RegisterForm;
